import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, Star } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { useLang } from "../i18n";

const StarPicker = ({ value, onChange, size = 34, testIdPrefix }) => (
  <div className="flex gap-1.5">
    {[1, 2, 3, 4, 5].map((i) => (
      <button key={i} type="button" data-testid={`${testIdPrefix}-star-${i}`} onClick={() => onChange(i)}
        className="transition-transform hover:scale-110" aria-label={`${i}/5`}>
        <Star size={size} className={i <= value ? "fill-amber-400 text-amber-400" : "text-slate-300"} />
      </button>
    ))}
  </div>
);

export default function Review() {
  const { token } = useParams();
  const { t } = useLang();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [rating, setRating] = useState(5);
  const [criteria, setCriteria] = useState({ punctuality: 5, comfort: 5, courtesy: 5 });
  const [name, setName] = useState("");
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [googleUrl, setGoogleUrl] = useState("");
  const [done, setDone] = useState(false);

  const CRITERIA = [
    { key: "punctuality", label: t("review.crit_punctuality") },
    { key: "comfort", label: t("review.crit_comfort") },
    { key: "courtesy", label: t("review.crit_courtesy") },
  ];

  useEffect(() => {
    api.get(`/reviews/booking/${token}`)
      .then((r) => {
        setInfo(r.data);
        setName(r.data.name);
      })
      .catch(() => setError(t("review.invalid_title")));
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async () => {
    if (name.trim().length < 2) return toast.error(t("review.err_name"));
    if (comment.trim().length < 4) return toast.error(t("review.err_comment"));
    setSubmitting(true);
    try {
      const { data } = await api.post("/reviews", {
        token, rating, author_name: name, comment,
        punctuality: criteria.punctuality, comfort: criteria.comfort, courtesy: criteria.courtesy,
      });
      setGoogleUrl(data.google_review_url || "");
      setDone(true);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const fieldCls =
    "w-full rounded-xl border border-slate-200 px-4 py-3 text-sm font-medium text-navy outline-none focus:border-azure focus:ring-2 focus:ring-azure/15";

  return (
    <div className="flex min-h-screen flex-col bg-sand" data-testid="review-page">
      <Navbar />
      <main className="mx-auto flex w-full max-w-xl flex-1 items-center px-4 pb-24 pt-32 sm:px-6">
        {!info && !error && (
          <div className="flex w-full justify-center"><Loader2 size={32} className="animate-spin text-azure" /></div>
        )}

        {error && (
          <div className="w-full rounded-3xl bg-white p-12 text-center shadow-xl" data-testid="review-error">
            <h1 className="font-serif text-2xl font-semibold text-navy">{t("review.invalid_title")}</h1>
            <p className="mt-3 text-sm text-navy/60">{error}</p>
          </div>
        )}

        {info && !done && !error && (
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
            className="w-full rounded-3xl border border-slate-100 bg-white p-8 shadow-xl sm:p-10" data-testid="review-form">
            <p className="overline-label mb-2">{t("review.overline")}</p>
            <h1 className="font-serif text-2xl font-semibold text-navy sm:text-3xl">{t("review.title")}</h1>
            <p className="mt-2 text-sm text-navy/55">{info.pickup_label} → {info.dropoff_label} · {info.date}</p>

            {!info.completed && (
              <p className="mt-6 rounded-2xl bg-amber-50 p-4 text-sm text-amber-800" data-testid="review-not-completed">
                {t("review.not_completed")}
              </p>
            )}
            {info.already_reviewed && (
              <p className="mt-6 rounded-2xl bg-azure-soft p-4 text-sm text-navy/70" data-testid="review-already">
                {t("review.already")}
              </p>
            )}

            {info.completed && !info.already_reviewed && (
              <div className="mt-8 space-y-7">
                <div>
                  <p className="mb-2 text-sm font-bold text-navy">{t("review.global")}</p>
                  <StarPicker value={rating} onChange={setRating} testIdPrefix="review" />
                </div>

                <div className="space-y-3 rounded-2xl bg-sand p-5">
                  {CRITERIA.map((c) => (
                    <div key={c.key} className="flex items-center justify-between gap-4">
                      <span className="text-sm font-medium text-navy/70">{c.label}</span>
                      <StarPicker value={criteria[c.key]} size={20} testIdPrefix={`review-${c.key}`}
                        onChange={(v) => setCriteria((s) => ({ ...s, [c.key]: v }))} />
                    </div>
                  ))}
                </div>

                <div>
                  <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-navy/50">{t("review.name_label")}</label>
                  <input data-testid="review-name-input" value={name} onChange={(e) => setName(e.target.value)} className={fieldCls} />
                </div>

                <div>
                  <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-navy/50">{t("review.comment_label")}</label>
                  <textarea data-testid="review-comment-input" rows={4} value={comment} onChange={(e) => setComment(e.target.value)}
                    placeholder={t("review.comment_ph")} className={`${fieldCls} resize-none`} />
                </div>

                <button data-testid="review-submit-button" onClick={submit} disabled={submitting}
                  className="flex w-full items-center justify-center gap-2 rounded-full bg-azure py-4 text-sm font-bold text-white shadow-lg shadow-azure/25 transition-colors hover:bg-azure-hover disabled:opacity-60">
                  {submitting && <Loader2 size={16} className="animate-spin" />} {t("review.submit")}
                </button>
              </div>
            )}
          </motion.div>
        )}

        {done && (
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
            className="w-full rounded-3xl border border-slate-100 bg-white p-12 text-center shadow-xl" data-testid="review-success">
            <CheckCircle2 size={52} className="mx-auto text-emerald-500" />
            <h1 className="mt-6 font-serif text-3xl font-semibold text-navy">{t("review.thanks_title", { name: name.split(" ")[0] })}</h1>
            <p className="mx-auto mt-3 max-w-sm text-sm leading-relaxed text-navy/60">{t("review.thanks_text")}</p>
            {rating >= 4 && googleUrl && (
              <a href={googleUrl} target="_blank" rel="noopener noreferrer" data-testid="google-review-link"
                className="mt-8 block rounded-full border-2 border-azure py-4 text-sm font-bold text-azure transition-colors hover:bg-azure hover:text-white">
                {t("review.google_cta")}
              </a>
            )}
            <Link to="/" className="mt-4 inline-block text-sm font-semibold text-navy/50 hover:text-azure">
              {t("common.back_home")}
            </Link>
          </motion.div>
        )}
      </main>
      <Footer />
    </div>
  );
}
